if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        this.initTimer = setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      clearTimeout(this.initTimer);
      this.mediaRequest?.abort();
      this.removeListeners();
    }

    removeListeners() {
      this.section?.removeEventListener('on:variant:change', this.boundVariantChange);
      window.removeEventListener('on:debounced-resize', this.resizeHandler);
    
      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }
    
      if (this.zoomInitHandler) {
        window.removeEventListener('on:breakpoint-change', this.zoomInitHandler);
      }
    
      if (this.viewer && this.boundHandleScroll) {
        this.viewer.removeEventListener('scroll', this.boundHandleScroll);
      }
    
      if (this.controls && this.boundHandleNavClick) {
        this.controls.removeEventListener('click', this.boundHandleNavClick);
      }
    
      if (this.thumbs && this.boundHandleThumbClick) {
        this.thumbs.removeEventListener('click', this.boundHandleThumbClick);
      }
    
      if (this.thumbs && this.boundHandleThumbTouch) {
        this.thumbs.removeEventListener('touchend', this.boundHandleThumbTouch);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');
      this.hideVariantMediaItems();
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        if (this.stackedScroll === 'always') {
          this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
          setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, true), 200);
        }
      } else {
        this.initGallery();
      }
      if (this.zoomLinks) {
        this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
        window.addEventListener('on:breakpoint-change', this.zoomInitHandler);
        this.initZoom();
        this.zoomLinks.forEach((el) => {
          el.addEventListener('click', (evt) => {
            evt.preventDefault();
          });
        });
      }
      this.tabs = this.querySelectorAll('.product-media-tab');

      if (this.tabs.length) {
        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            const type = tab.dataset.tab;

            this.tabs.forEach((item) => item.classList.remove('is-active'));
            tab.classList.add('is-active');

            this.filterMediaByTab(type);
          });
        });

        this.filterMediaByTab('image');
      }
      this.boundVariantChange = this.boundVariantChange || this.onVariantChange.bind(this);
      this.section.addEventListener('on:variant:change', this.boundVariantChange);
      if (this.hasAttribute('data-variant-media')) {
        this.addListeners();
        this.filterMediaByTab('image');
      }
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.hasAttribute('data-variant-media')) {
        this.updateVariantMedia(evt.detail.variant);
        return;
      }
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }
    
      this.hideVariantMediaItems();
    
      if (evt.detail.variant) {
        this.updateMainMediaImage(evt.detail.variant);
      }
    }

    async updateVariantMedia(variant) {
      this.mediaRequest?.abort();
      this.removeAttribute('aria-busy');
      if (!variant || String(variant.id) === this.dataset.variantId) return;

      const request = new AbortController();
      this.mediaRequest = request;
      window.pauseAllMedia(this);
      this.setAttribute('aria-busy', 'true');
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.search = window.location.search;
      url.searchParams.delete('option_values');
      url.searchParams.delete('sections');
      url.searchParams.set('variant', variant.id);
      url.searchParams.set('section_id', this.dataset.sectionId);

      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error(`Media gallery request failed: ${response.status}`);
        const html = await response.text();
        if (request.signal.aborted || !this.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const gallery = Array.from(doc.querySelectorAll('media-gallery[data-variant-media]'))
          .find((element) => element.dataset.sectionId === this.dataset.sectionId
            && element.dataset.variantId === String(variant.id));
        if (!gallery) throw new Error('The selected variant media gallery was not returned');

        window.pauseAllMedia(this);
        this.removeListeners();
        const coupon = this.querySelector('.coupon-label');
        const nextCoupon = gallery.querySelector('.coupon-label');
        if (coupon && nextCoupon) nextCoupon.replaceWith(coupon);
        this.replaceChildren(...gallery.childNodes);
        this.querySelectorAll('img[loading="lazy"]').forEach((img) => setImageSources(img));
        this.dataset.baseMainMediaId = gallery.dataset.baseMainMediaId;
        this.dataset.variantId = gallery.dataset.variantId;
        this.toggleAttribute('data-media-grouping-enabled', gallery.hasAttribute('data-media-grouping-enabled'));
        this.currentItem = null;
        this.currentThumb = null;
        this.currentGroup = null;
        this.mediaGroupMap = null;
        this.variantMediaData = undefined;
        this.init();
      } catch (error) {
        if (request.signal.aborted || !this.isConnected) return;
        console.error(error);
        url.searchParams.delete('section_id');
        window.location.assign(url.href);
      } finally {
        if (this.mediaRequest === request) this.removeAttribute('aria-busy');
      }
    }
    hideVariantMediaItems() {
      const viewerItems = this.querySelectorAll('.media-viewer__item');
      const thumbItems = this.querySelectorAll('.media-thumbs__item');
      const baseMainMediaId = this.getBaseMainMediaId();
    
      viewerItems.forEach((item) => {
        const isVariantImage = item.dataset.isVariantImage === 'true';
        const isImageKind = item.dataset.mediaKind === 'image';
        const isMainMedia = String(item.dataset.mediaId) === baseMainMediaId;
    
        if (isMainMedia) {
          item.style.display = '';
          item.removeAttribute('aria-hidden');
          return;
        }
    
        if (isImageKind && isVariantImage) {
          item.style.display = 'none';
          item.setAttribute('aria-hidden', 'true');
        }
      });
    
      thumbItems.forEach((item) => {
        const isVariantImage = item.dataset.isVariantImage === 'true';
        const isImageKind = item.dataset.mediaKind === 'image';
        const isMainThumb = String(item.dataset.mediaId) === baseMainMediaId;
    
        if (isMainThumb) {
          item.style.display = '';
          item.removeAttribute('aria-hidden');
          return;
        }
    
        if (isImageKind && isVariantImage) {
          item.style.display = 'none';
          item.setAttribute('aria-hidden', 'true');
        }
      });
    }
    refreshGalleryState() {
      this.setVisibleItems();
    
      if (!this.visibleItems || !this.visibleItems.length) {
        this.currentItem = null;
        this.currentIndex = -1;
    
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        if (this.counterCurrent) this.counterCurrent.textContent = 0;
        if (this.counterTotal) this.counterTotal.textContent = 0;
        return;
      }
    
      const firstVisible = this.visibleItems[0];
      this.currentItem = firstVisible;
      this.currentIndex = 0;
    
      if (this.counterCurrent) this.counterCurrent.textContent = 1;
      if (this.counterTotal) this.counterTotal.textContent = this.visibleItems.length;
    
      if (this.prevBtn) this.prevBtn.disabled = true;
      if (this.nextBtn) this.nextBtn.disabled = this.visibleItems.length <= 1;
    
      if (this.thumbs) {
        this.currentThumb = this.getBaseMainThumbItem();
        if (this.currentThumb) {
          this.setActiveThumb();
        }
      }
    }
    getBaseMainMediaId() {
      return this.dataset.baseMainMediaId ? String(this.dataset.baseMainMediaId) : '';
    }
    
    getBaseMainViewerItem() {
      const baseMainMediaId = this.getBaseMainMediaId();
    
      if (baseMainMediaId && this.viewer) {
        const item = this.viewer.querySelector(
          `.media-viewer__item[data-media-id="${baseMainMediaId}"]`
        );
        if (item) return item;
      }
    
      return this.viewer
        ? this.viewer.querySelector('.media-viewer__item[data-is-main-media="true"]')
          || this.viewer.querySelector('.media-viewer__item')
        : null;
    }
    
    getBaseMainThumbItem() {
      const baseMainMediaId = this.getBaseMainMediaId();
    
      if (baseMainMediaId) {
        const item = this.querySelector(
          `.media-thumbs__item[data-media-id="${baseMainMediaId}"]`
        );
        if (item) return item;
      }
    
      return this.querySelector('.media-thumbs__item[data-is-main-thumb="true"]');
    }
    getThumbSrcWidth() {
      return parseInt(this.dataset.thumbSrcWidth, 10) || 80;
    }
    
    getMainImageSrcWidth(img) {
      if (!img) return 1214;
    
      const attrWidth = parseInt(img.getAttribute('width'), 10);
      if (attrWidth) return attrWidth;
    
      const renderedWidth = Math.ceil((img.clientWidth || 0) * (window.devicePixelRatio || 1));
      if (renderedWidth) return renderedWidth;
    
      return 1214;
    }
    
    buildSizedImageUrl(src, width) {
      if (!src) return '';
    
      const safeWidth = parseInt(width, 10) || 80;
    
      if (src.includes('width=')) {
        return src.replace(/width=\d+/i, `width=${safeWidth}`);
      }
    
      return src.includes('?')
        ? `${src}&width=${safeWidth}`
        : `${src}?width=${safeWidth}`;
    }
    buildSrcset(urls) {
      return urls
        .filter((item) => item.url && item.width)
        .map((item) => `${item.url} ${item.width}w`)
        .join(', ');
    }
    
    updatePictureSources(pictureEl, rawSrc, desktopWidth) {
      if (!pictureEl || !rawSrc) return;
    
      const sourceEls = pictureEl.querySelectorAll('source');
    
      sourceEls.forEach((sourceEl) => {
        const mediaAttr = sourceEl.getAttribute('media') || '';
        let newSrcset = '';
    
        // 主题 image.liquid 里 mobile source 固定是 690 / 800
        if (mediaAttr.includes('max-width: 600px')) {
          newSrcset = this.buildSrcset([
            { url: this.buildSizedImageUrl(rawSrc, 690), width: 690 },
            { url: this.buildSizedImageUrl(rawSrc, 800), width: 800 }
          ]);
    
          if (sourceEl.hasAttribute('srcset')) {
            sourceEl.setAttribute('srcset', newSrcset);
          }
          if (sourceEl.hasAttribute('data-srcset')) {
            sourceEl.setAttribute('data-srcset', newSrcset);
          }
          return;
        }
    
        // 其他 source，按桌面主图宽度兜底
        newSrcset = this.buildSrcset([
          { url: this.buildSizedImageUrl(rawSrc, desktopWidth), width: desktopWidth }
        ]);
    
        if (sourceEl.hasAttribute('srcset')) {
          sourceEl.setAttribute('srcset', newSrcset);
        }
        if (sourceEl.hasAttribute('data-srcset')) {
          sourceEl.setAttribute('data-srcset', newSrcset);
        }
      });
    }
    /**
     * 不切换到 variant.featured_media 对应的 gallery item，
     * 只替换 gallery 第一张主图里的图片地址。
     * @param {object} variant
     */
    updateMainMediaImage(variant) {
      if (this.hasAttribute('data-variant-media')) return;
      if (!variant || !variant.featured_media || !this.viewer) return;
    
      const mediaType = variant.featured_media.media_type || variant.featured_media.mediaType || 'image';
      if (mediaType !== 'image') return;
    
      const rawSrc =
        variant.featured_media.preview_image?.src ||
        variant.featured_media.src ||
        variant.featured_image?.src ||
        '';
    
      if (!rawSrc) return;
    
      const firstViewerItem = this.getBaseMainViewerItem();
    
      if (!firstViewerItem) return;
    
      const mainImg =
        firstViewerItem.querySelector('.product-image') ||
        firstViewerItem.querySelector('.media img') ||
        firstViewerItem.querySelector('img');

      if (mainImg) {
        const mainWidth = this.getMainImageSrcWidth(mainImg);
        const mainSrc = this.buildSizedImageUrl(rawSrc, mainWidth);
        const mainPicture = mainImg.closest('picture');

        // 关键：先更新 picture 里的 source，尤其是手机端用到的 mobile source
        if (mainPicture) {
          this.updatePictureSources(mainPicture, rawSrc, mainWidth);
        }

        if (mainImg.hasAttribute('src')) {
          mainImg.src = mainSrc;
        } else {
          mainImg.setAttribute('src', mainSrc);
        }

        if (mainImg.hasAttribute('data-src')) {
          mainImg.setAttribute('data-src', mainSrc);
        }

        // 主题 image.liquid 会输出 srcset / data-srcset，这里都要一起处理
        if (mainImg.hasAttribute('srcset')) {
          mainImg.setAttribute('srcset', '');
        }
        if (mainImg.hasAttribute('data-srcset')) {
          mainImg.setAttribute('data-srcset', '');
        }

        mainImg.alt = variant.featured_media.alt || variant.title || mainImg.alt || '';

        if (mainImg.dataset) {
          if (mainImg.dataset.src) mainImg.dataset.src = mainSrc;
          if (mainImg.dataset.originalSrc) mainImg.dataset.originalSrc = mainSrc;
          if (mainImg.dataset.zoomSrc) {
            mainImg.dataset.zoomSrc = this.buildSizedImageUrl(rawSrc, 2000);
          }
        }
      }
    
      const zoomLink = firstViewerItem.querySelector('.js-zoom-link');
      if (zoomLink) {
        zoomLink.href = this.buildSizedImageUrl(rawSrc, 2000);
      }
    
      const zoomImage = firstViewerItem.querySelector('.js-zoom-image');
      if (zoomImage) {
        const zoomSrc = this.buildSizedImageUrl(rawSrc, 2000);
    
        if (zoomImage.dataset && zoomImage.dataset.src) {
          zoomImage.dataset.src = zoomSrc;
        } else {
          zoomImage.src = zoomSrc;
        }
    
        zoomImage.alt = variant.featured_media.alt || variant.title || zoomImage.alt || '';
      }
    
      const firstThumbItem = this.getBaseMainThumbItem();
      if (firstThumbItem) {
        const thumbImg = firstThumbItem.querySelector('img');
    
        if (thumbImg) {
          const thumbSrc = this.buildSizedImageUrl(rawSrc, this.getThumbSrcWidth());
    
          thumbImg.src = thumbSrc;
          thumbImg.alt = variant.featured_media.alt || variant.title || thumbImg.alt || '';
    
          if (thumbImg.srcset) thumbImg.srcset = '';
          if (thumbImg.dataset) {
            if (thumbImg.dataset.src) thumbImg.dataset.src = thumbSrc;
            if (thumbImg.dataset.originalSrc) thumbImg.dataset.originalSrc = thumbSrc;
          }
        }
      }
    
      this.hideVariantMediaItems();
      this.refreshGalleryState();
      this.customSetActiveMedia(firstViewerItem, false);
    
      if (this.viewer) {
        this.viewer.scrollTo({
          left: firstViewerItem.offsetLeft,
          behavior: 'auto'
        });
      }
    }
    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
        this.hideVariantMediaItems();
      }
    }
    filterMediaByTab(type) {
      window.pauseAllMedia(this);
      const viewerItems = this.querySelectorAll('.media-viewer__item');
      const thumbItems = this.querySelectorAll('.media-thumbs__item');
    
      viewerItems.forEach((el) => {
        const kind = el.dataset.mediaKind;
        el.style.display = kind === type ? '' : 'none';
      });
    
      thumbItems.forEach((el) => {
        const kind = el.dataset.mediaKind;
        el.style.display = kind === type ? '' : 'none';
      });
    
      // 关键：tab 切换后要再次隐藏“非主图位”的变体图片，
      // 否则 image tab 会把之前隐藏掉的变体图重新显示出来
      if (type === 'image') {
        this.hideVariantMediaItems();
      }
    
      this.setVisibleItems();
      this.viewerItemOffset = this.visibleItems.length > 1
        ? this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft
        : 0;
    
      if (!this.visibleItems.length) {
        this.currentItem = null;
        this.currentIndex = -1;
    
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        if (this.counterCurrent) this.counterCurrent.textContent = 0;
        if (this.counterTotal) this.counterTotal.textContent = 0;
        return;
      }
    
      const baseMainItem = this.getBaseMainViewerItem();
      const firstVisible = (baseMainItem && this.visibleItems.includes(baseMainItem))
        ? baseMainItem
        : this.visibleItems[0];
    
      this.currentItem = null;
      this.customSetActiveMedia(firstVisible, false);
    
      if (this.viewer && typeof firstVisible.offsetLeft !== 'undefined') {
        this.viewer.scrollTo({ left: firstVisible.offsetLeft, behavior: 'auto' });
      }
    
      if (this.prevBtn) {
        this.prevBtn.disabled = this.visibleItems.indexOf(firstVisible) <= 0;
      }
    
      if (this.nextBtn) {
        this.nextBtn.disabled = this.visibleItems.length <= 1;
      }
    }
    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.hideVariantMediaItems();
      this.setVisibleItems();
    
      // 没有任何可见媒体，直接退出
      if (!this.visibleItems.length) {
        this.currentItem = null;
        this.currentIndex = -1;
    
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        if (this.counterCurrent) this.counterCurrent.textContent = 0;
        if (this.counterTotal) this.counterTotal.textContent = 0;
        return;
      }
    
      // 至少先把当前项初始化出来，避免后续空指针
      const currentItem = this.querySelector('.media-viewer__item.is-current-variant')
        || this.visibleItems[0];
    
      this.currentItem = currentItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);
    
      if (this.currentIndex < 0) {
        this.currentItem = this.visibleItems[0];
        this.currentIndex = 0;
      }
    
      // 只有一个可见媒体时，不初始化滑动逻辑，但要把状态补齐
      if (this.visibleItems.length === 1) {
        this.viewerItemOffset = 0;
    
        if (this.thumbs && this.currentItem) {
          this.currentThumb = this.thumbs.querySelector(
            `[data-media-id="${this.currentItem.dataset.mediaId}"]`
          );
        }
    
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        if (this.counterCurrent) this.counterCurrent.textContent = 1;
        if (this.counterTotal) this.counterTotal.textContent = 1;
    
        this.customSetActiveMedia(this.currentItem, false);
        return;
      }
    
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex] || this.visibleItems[0];
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);
    
      this.addListeners();
    
      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }
    
      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }
    
      this.customSetActiveMedia(this.currentItem, true);
    }

    addListeners() {
      this.boundHandleScroll = this.boundHandleScroll || this.handleScroll.bind(this);
      this.boundHandleNavClick = this.boundHandleNavClick || this.handleNavClick.bind(this);
      this.boundHandleThumbClick = this.boundHandleThumbClick || this.handleThumbClick.bind(this);
      this.boundHandleThumbTouch = this.boundHandleThumbTouch || this.handleThumbTouch.bind(this);
    
      if (this.viewer) {
        this.viewer.removeEventListener('scroll', this.boundHandleScroll);
        this.viewer.addEventListener('scroll', this.boundHandleScroll);
      }
    
      if (this.controls) {
        this.controls.removeEventListener('click', this.boundHandleNavClick);
        this.controls.addEventListener('click', this.boundHandleNavClick);
      }
    
      if (this.thumbs) {
        this.thumbs.removeEventListener('click', this.boundHandleThumbClick);
        this.thumbs.removeEventListener('touchend', this.boundHandleThumbTouch);
    
        this.thumbs.addEventListener('click', this.boundHandleThumbClick);
        this.thumbs.addEventListener('touchend', this.boundHandleThumbTouch, { passive: false });
      }
    
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        if (theme.mediaMatches.md) {
          el.addEventListener('mousemove', this.handleZoomMouseMove.bind(this));
        } else {
          el.removeEventListener('mousemove', this.handleZoomMouseMove.bind(this));
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      if (!this.viewer || !this.visibleItems || this.visibleItems.length < 2 || !this.viewerItemOffset) {
        return;
      }
    
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
    
      if (newIndex !== this.currentIndex && this.visibleItems[newIndex]) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
    
        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      const btn = evt.target.closest('.media-ctrl__btn');
      if (!btn) return;
      if (!this.viewer || !this.visibleItems || !this.visibleItems.length) return;
    
      const itemToShow = btn === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];
    
      if (!itemToShow) return;
    
      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumbBtn = evt.target.closest('.media-thumbs__btn');
      if (!thumbBtn) return;
    
      const thumbItem = thumbBtn.closest('.media-thumbs__item');
      if (!thumbItem) return;
    
      const mediaId = thumbItem.dataset.mediaId;
      if (!mediaId) return;
    
      const itemToShow = this.viewer
        ? this.viewer.querySelector(`.media-viewer__item[data-media-id="${mediaId}"]`)
        : null;
    
      if (!itemToShow) return;
      if (itemToShow.style.display === 'none') return;
    
      this.customSetActiveMedia(itemToShow, true);
      MediaGallery.playActiveMedia(itemToShow);
    }
    handleThumbTouch(evt) {
      evt.preventDefault();
      this.handleThumbClick(evt);
    }
    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      this.setVisibleItems();
    
      if (!this.visibleItems || this.visibleItems.length < 2) {
        this.viewerItemOffset = 0;
    
        if (this.thumbs && this.currentThumb) {
          this.checkThumbVisibilty(this.currentThumb);
        }
        return;
      }
    
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
    
      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (!mediaItem || !this.viewer) return;
      if (mediaItem === this.currentItem) return;
    
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems ? this.visibleItems.indexOf(this.currentItem) : -1;
    
      // 当前项不在可见列表里时，回退到第一个可见项
      if (this.currentIndex < 0 && this.visibleItems && this.visibleItems.length) {
        this.currentItem = this.visibleItems[0];
        this.currentIndex = 0;
      }
    
      if (!this.currentItem) return;
    
      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        if (this.stackedScroll !== 'never' && scrollToItem) {
          const y = this.currentItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;
      
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'auto'
            });
          }
        }
        if (!this.hasAttribute('data-variant-media')) return;
      }
    
      if (scrollToItem && this.currentItem) {
        this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      }
    
      if (this.thumbs) this.setActiveThumb();
    
      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex <= 0;
        }
    
        if (this.nextBtn) {
          this.nextBtn.disabled = !this.visibleItems || this.currentIndex >= this.visibleItems.length - 1;
        }
    
        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex >= 0 ? this.currentIndex + 1 : 0;
        }
      }
    
      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);
    
      if (this.xrButton && this.currentItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = this.currentItem.dataset.mediaId;
      }
    
      this.dispatchEvent(new CustomEvent('media-gallery:change', {
        detail: {
          index: this.currentIndex + 1,
          total: this.visibleItems ? this.visibleItems.length : 0,
          mediaId: this.currentItem.dataset.mediaId,
          mediaItem: this.currentItem
        }
      }));
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      if (!this.thumbs || !this.currentItem) return;
    
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
    
      if (!this.currentThumb) return;
    
      const btn = this.currentThumb.querySelector('button');
      if (!btn) return;
    
      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });
    
      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }
    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
      this.querySelectorAll('[data-fancybox], [data-gallery-fancybox]').forEach((link) => {
        const item = link.closest('.media-viewer__item');
        if (item && item.style.display === 'none') {
          link.dataset.galleryFancybox = link.dataset.fancybox || link.dataset.galleryFancybox;
          link.removeAttribute('data-fancybox');
        } else if (link.dataset.galleryFancybox) {
          link.dataset.fancybox = link.dataset.galleryFancybox;
          link.removeAttribute('data-gallery-fancybox');
        }
      });
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      if (!this.thumbs || !thumb) return;
    
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;
    
      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
